import { readDeepSearchProgressEvent } from "@frontend-shared/lib";
import type {
  IChatHistoryPage,
  IDeepSearchProgressPayload,
  IIntegrationConnectPromptPayload,
  IKnowledgeSourcesPayload,
  IMessage,
  IMessageContentUpdatePayload,
  IMessageDeltaPayload,
  IMessagePreparationProgressPayload,
  IReasoningStepPayload,
  IStreamErrorPayload,
  IStreamFinishPayload,
  IToolApprovalRequestPayload,
  IToolApprovalRespondedPayload,
  IToolCallEndPayload,
  IToolCallPayload,
  IToolFilePayload,
  IWebSearchProgressPayload,
  TSkillActivationsEvent,
  TSkillScriptRunEvent,
} from "@frontend-shared/types";
import {
  findMessageLocationInPages,
  setMessageToolStepStatusInPages,
  updateFirstPageInChatHistory,
} from "@frontend-shared/utils";
import type { InfiniteData } from "@tanstack/react-query";

import { chatHistoryQueryKey } from "@/api/chat/chat-history-query-key";
import { BACKGROUND_TRACKING_TIMEOUT_MS } from "@/constants/websocket";

import {
  applyIntegrationConnectPromptToChatHistory,
  applySkillActivationsToMessage,
  applySkillScriptRunToMessage,
  applyToolApprovalRespondedToMessage,
  cancelSearchProgressForChat,
  cancelSearchProgressForMessageInPages,
  clearMessageStreamError,
  clearMessageToolCallInPages,
  hasActiveServerStreamInPages,
  hasActiveWorkInPages,
  setMessageDeepSearchProgress,
  setMessageKnowledgeSources,
  setMessagePreparationProgress,
  setMessageStreamError,
  setMessageStreamFinished,
  setMessageToolCall,
  setMessageWebSearchProgress,
  updateChatHistoryMessage,
  updateMessageInPages,
  upsertNewMessagesIntoChatHistory,
  upsertToolApprovalRequestToMessage,
} from "./chat-cache-utils";
import { enqueueChatStreamChunk } from "./chat-stream-buffer";
import { queryClient } from "./query-client";
import type {
  IBackgroundStreamTracker,
  IRegisterHandlerProps,
  IStartBackgroundTrackingProps,
} from "./types";
import { websocketService } from "./websocket";
import { setActiveStreamProbe } from "./websocket-active-stream-probe";

// NOTE: NSAI-5117 — extracted from the web copy of WebSocketService so the web
// app can consume the shared transport (apps/nsai/frontend-shared/api/websocket.ts)
// without dragging React Query into that platform-agnostic package.
//
// What this does: when the user navigates away from a chat whose reply is still
// generating, the room is kept open for up to BACKGROUND_TRACKING_TIMEOUT_MS and
// every arriving frame is written into the React Query cache, so the completed
// answer is already on screen when they come back.
const backgroundTrackers = new Map<string, IBackgroundStreamTracker>();

/**
 * True when the foreground chat or any background-tracked chat still has an
 * actively streaming assistant message. Supplied to the shared service as
 * `hasActiveStream`, which uses it to avoid cycling a live handshake mid-reply
 * during an auth refresh. Pending tool approvals are deliberately excluded so
 * those flows reconnect promptly rather than deadlocking on an expired token.
 */
export const hasActiveStreamInTrackedChats = (): boolean => {
  const chatIds = new Set<string>();
  const activeChatId = websocketService.activeChatId;
  if (activeChatId) {
    chatIds.add(activeChatId);
  }
  for (const [backgroundChatId] of backgroundTrackers) {
    chatIds.add(backgroundChatId);
  }

  for (const chatId of chatIds) {
    const cachedData = queryClient.getQueryData<InfiniteData<IChatHistoryPage>>(
      chatHistoryQueryKey(chatId),
    );
    if (cachedData && hasActiveServerStreamInPages(cachedData.pages)) {
      return true;
    }
  }

  return false;
};

// NOTE: NSAI-5117 — a reconnect (automatic or deliberate) lands in a fresh
// socket that has joined no rooms. The shared service re-attaches the event
// subscriptions from its registry, but room membership is server-side state and
// must be re-entered explicitly. `rejoinChatRoom` deliberately does not claim
// `currentChatId` — the foreground chat owns that.
//
websocketService.onConnect(() => {
  for (const [backgroundChatId] of backgroundTrackers) {
    websocketService.rejoinChatRoom(backgroundChatId);
  }
});

// NOTE: NSAI-5117 — hand the service the answer to "is a reply still streaming?"
// so a token refresh mid-reply defers its reconnect instead of cutting the
// reply off.
setActiveStreamProbe(hasActiveStreamInTrackedChats);

// NOTE: NSAI-5117 — a client-initiated disconnect (language or project switch)
// invalidates every tracked room: the next socket authenticates under the new
// scope, so rejoining the old chats would be wrong. The local web class did
// this inside its own `disconnect()`; the trackers now live out here, so the
// service calls back instead. A transport drop is deliberately NOT covered —
// those rooms are legitimately re-joined by `onConnect` above.
websocketService.onBeforeDisconnect(() => {
  stopAllBackgroundTracking();
});

export const startBackgroundTracking = ({
  chatId,
}: IStartBackgroundTrackingProps): void => {
  if (backgroundTrackers.has(chatId)) {
    return;
  }

  if (!websocketService.hasSocket) {
    // NOTE: No socket means we cannot receive events — just leave normally
    websocketService.leaveChat(chatId);
    return;
  }

  console.warn("WebSocket: Starting background tracking for chat:", chatId);

  const unsubscribers: IBackgroundStreamTracker["unsubscribers"] = [];

  // NOTE: NSAI-5117 — subscriptions go through the shared service's public
  // `on*` API, which keeps them in its registry and re-attaches them after a
  // reconnect. The tracker only has to remember how to undo each one.
  const registerHandler = <T>({
    subscribe,
    handler,
  }: IRegisterHandlerProps<T>): void => {
    unsubscribers.push(subscribe(handler));
  };

  // NOTE: Factory for background handlers that update React Query cache.
  // Reduces duplication across stream event handlers that share the same
  // chatId-guard → copy-on-write → cache-update → setQueryData pattern.
  // NSAI-5908 — copy-on-write of the ONE message the event names (see
  // updateChatHistoryMessage) instead of a structuredClone of the whole
  // transcript per event.
  const queryKey = chatHistoryQueryKey(chatId);

  // NOTE: The updater receives the message the copy-on-write helper already
  // located (a clone, safe to mutate) next to the rebuilt pages, so no
  // handler has to search the transcript for it again.
  const createBgCacheUpdater = <
    T extends { chatUuid?: string; uuid?: string },
  >(props: {
    update: (updateProps: {
      message: IMessage;
      pages: IChatHistoryPage[];
      payload: T;
    }) => void;
    getUuid?: (payload: T) => string | undefined;
  }): ((payload: T) => void) => {
    const { update, getUuid } = props;
    return (payload: T): void => {
      const targetChatId = payload.chatUuid ?? chatId;
      if (targetChatId !== chatId) return;
      const uuid = getUuid ? getUuid(payload) : payload.uuid;
      if (!uuid) return;

      queryClient.setQueryData<InfiniteData<IChatHistoryPage>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;
          return updateChatHistoryMessage({
            oldData,
            uuid,
            mutate: ({ message, pages }) => {
              update({ message, pages, payload });
              return true;
            },
          });
        },
      );
    };
  };

  // NOTE: Wraps a cache updater as a terminal handler that triggers cleanup
  const createTerminalBgHandler = <
    T extends { chatUuid?: string; uuid?: string },
  >(props: {
    update: (updateProps: {
      message: IMessage;
      pages: IChatHistoryPage[];
      payload: T;
    }) => void;
  }): ((payload: T) => void) => {
    const updater = createBgCacheUpdater(props);
    return (payload: T): void => {
      updater(payload);
      cleanupBackgroundTrackerIfIdle(chatId);
    };
  };

  // NOTE: messageUpdated has unique logic (upsert + done-check) — kept separate
  registerHandler<IMessage & { tempId?: string }>({
    subscribe: (listener) => websocketService.onMessageUpdated(listener),
    handler: (message) => {
      if (message.chatUuid !== chatId) return;

      queryClient.setQueryData<InfiniteData<IChatHistoryPage>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;
          const existingUuid = message.tempId || message.uuid;
          const location = findMessageLocationInPages({
            pages: oldData.pages,
            uuid: existingUuid,
          });
          if (!location) {
            return updateFirstPageInChatHistory({
              oldData,
              update: (data) => [message, ...data],
            });
          }
          return updateChatHistoryMessage({
            oldData,
            uuid: existingUuid,
            mutate: ({ pages }) => {
              updateMessageInPages({ pages, message });
              if (message.done) {
                clearMessageStreamError({ pages, uuid: message.uuid });
              }
              return true;
            },
          });
        },
      );

      if (message.done) {
        cleanupBackgroundTrackerIfIdle(chatId);
      }
    },
  });
  // NOTE: NSAI-5908 — per-token frames go through the coalescing buffer
  // (applied per animation frame, about once a second while hidden) exactly
  // like the foreground hook's; the buffer drops them when no cache exists.
  registerHandler<IMessageDeltaPayload>({
    subscribe: (listener) => websocketService.onMessageDelta(listener),
    handler: (payload) => {
      if ((payload.chatUuid ?? chatId) !== chatId) return;
      enqueueChatStreamChunk({
        chatUuid: chatId,
        uuid: payload.uuid,
        delta: payload.delta,
      });
    },
  });
  registerHandler<IMessageContentUpdatePayload>({
    subscribe: (listener) => websocketService.onMessageContentUpdate(listener),
    handler: (payload) => {
      if ((payload.chatUuid ?? chatId) !== chatId) return;
      enqueueChatStreamChunk({
        chatUuid: chatId,
        uuid: payload.uuid,
        replacementContent: payload.content,
      });
    },
  });
  registerHandler<IToolCallPayload>({
    subscribe: (listener) => websocketService.onToolCall(listener),
    handler: createBgCacheUpdater({
      update: ({ pages, payload }) => {
        setMessageToolCall({
          pages,
          uuid: payload.uuid,
          toolName: payload.toolName,
          args: payload.args,
          ...(payload.toolCallId ? { toolCallId: payload.toolCallId } : {}),
        });
      },
    }),
  });
  registerHandler<IToolCallEndPayload>({
    subscribe: (listener) => websocketService.onToolCallEnd(listener),
    handler: createBgCacheUpdater({
      update: ({ pages, payload }) => {
        setMessageToolStepStatusInPages({
          pages,
          uuid: payload.uuid,
          toolCallId: payload.toolCallId,
          status: payload.status,
        });
      },
    }),
  });
  registerHandler<IReasoningStepPayload>({
    subscribe: (listener) => websocketService.onReasoningStep(listener),
    handler: (payload) => {
      if ((payload.chatUuid ?? chatId) !== chatId) return;
      enqueueChatStreamChunk({
        chatUuid: chatId,
        uuid: payload.uuid,
        reasoning: payload.reasoning,
      });
    },
  });
  registerHandler<IDeepSearchProgressPayload>({
    subscribe: (listener) => websocketService.onDeepSearchProgress(listener),
    handler: createBgCacheUpdater({
      update: ({ pages, payload }) => {
        setMessageDeepSearchProgress({
          pages,
          uuid: payload.uuid,
          ...readDeepSearchProgressEvent(payload),
        });
      },
    }),
  });
  registerHandler<IWebSearchProgressPayload>({
    subscribe: (listener) => websocketService.onWebSearchProgress(listener),
    handler: createBgCacheUpdater({
      update: ({ pages, payload }) => {
        setMessageWebSearchProgress({
          pages,
          uuid: payload.uuid,
          progress: {
            status: payload.status,
            message: payload.message,
            query: payload.query,
            queryIndex: payload.queryIndex,
            totalQueries: payload.totalQueries,
            completedCount: payload.completedCount,
          },
        });
      },
    }),
  });
  registerHandler<IMessagePreparationProgressPayload>({
    subscribe: (listener) =>
      websocketService.onMessagePreparationProgress(listener),
    handler: createBgCacheUpdater({
      update: ({ pages, payload }) => {
        setMessagePreparationProgress({
          pages,
          uuid: payload.uuid,
          progress: {
            status: payload.status,
            message: payload.message,
          },
        });
      },
    }),
  });
  registerHandler<IKnowledgeSourcesPayload>({
    subscribe: (listener) => websocketService.onKnowledgeSources(listener),
    handler: createBgCacheUpdater({
      update: ({ pages, payload }) => {
        // NOTE: NSAI-5862 — forward contextChatSources like the foreground
        // handler does, or a turn finished on another page lost them.
        setMessageKnowledgeSources({
          pages,
          uuid: payload.uuid,
          sources: payload.sources,
          ...(payload.contextChatSources !== undefined && {
            contextChatSources: payload.contextChatSources,
          }),
        });
      },
    }),
  });
  // NOTE: NSAI-5681 — registered here as well as in the foreground hook (caught
  // in review on PR #5192, mirrors the skillActivations/skillScriptRun note
  // above), or a "connect this app" card raised while the user is on another
  // page would not appear live on return — only on the next full history
  // fetch, since only the persisted column (not the socket event) survives
  // a background tab.
  registerHandler<IIntegrationConnectPromptPayload>({
    subscribe: (listener) =>
      websocketService.onIntegrationConnectPrompt(listener),
    handler: (payload) => {
      if ((payload.chatUuid ?? chatId) !== chatId) return;
      queryClient.setQueryData<InfiniteData<IChatHistoryPage>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;
          // NOTE: NSAI-5681 — see the foreground hook: the note rides with
          // the card, so a resolution that lands while the user is on another
          // page brings its transcript line back with it.
          return applyIntegrationConnectPromptToChatHistory({
            oldData,
            payload,
          });
        },
      );
    },
  });
  // NOTE: NSAI-3308 — registered here as well as in the foreground hook, or a
  // user who navigates away mid-stream loses the activation timeline for the
  // reply that was still generating.
  registerHandler<TSkillActivationsEvent>({
    subscribe: (listener) => websocketService.onSkillActivations(listener),
    handler: createBgCacheUpdater({
      getUuid: (payload) => payload.messageUuid,
      update: ({ pages, payload }) => {
        applySkillActivationsToMessage({
          pages,
          uuid: payload.messageUuid,
          activations: payload.activations,
        });
      },
    }),
  });
  // NOTE: NSAI-3304 — registered here as well as in the foreground hook, or a user
  // who navigates away mid-stream loses the script-run block for the reply that was
  // still generating.
  registerHandler<TSkillScriptRunEvent>({
    subscribe: (listener) => websocketService.onSkillScriptRun(listener),
    handler: createBgCacheUpdater({
      getUuid: (payload) => payload.messageUuid,
      update: ({ pages, payload }) => {
        applySkillScriptRunToMessage({
          pages,
          uuid: payload.messageUuid,
          run: payload.run,
        });
      },
    }),
  });
  registerHandler<IStreamFinishPayload>({
    subscribe: (listener) => websocketService.onStreamFinish(listener),
    handler: createTerminalBgHandler({
      update: ({ pages, payload }) => {
        setMessageStreamFinished({ pages, uuid: payload.uuid });
        if (payload.reason === "cancelled") {
          clearMessageStreamError({ pages, uuid: payload.uuid });
          cancelSearchProgressForMessageInPages({
            pages,
            uuid: payload.uuid,
          });
          clearMessageToolCallInPages({ pages, uuid: payload.uuid });
        }
      },
    }),
  });
  registerHandler<IStreamErrorPayload>({
    subscribe: (listener) => websocketService.onStreamError(listener),
    handler: createTerminalBgHandler({
      update: ({ pages, payload }) => {
        setMessageStreamError({
          pages,
          uuid: payload.uuid,
          errorMessage: payload.error,
        });
      },
    }),
  });

  // NOTE: Agent workflows produce multiple message pairs per turn.
  // Without this handler, new turns arriving while the user is away
  // would be lost from the React Query cache.
  registerHandler<IMessage[]>({
    subscribe: (listener) => websocketService.onNewMessage(listener),
    handler: (messages) => {
      const lastIncomingMessage = messages.at(-1);
      if (!lastIncomingMessage) return;
      if ((lastIncomingMessage.chatUuid ?? chatId) !== chatId) return;

      const reversedMessages = messages.toReversed();
      queryClient.setQueryData<InfiniteData<IChatHistoryPage>>(
        queryKey,
        (oldData) =>
          upsertNewMessagesIntoChatHistory({
            oldData,
            reversedMessages,
            lastMessageUuid: lastIncomingMessage.uuid,
          }),
      );
    },
  });

  // NOTE: Agents may request tool approval while the user is on another page.
  // Cache the request so the approval UI appears when the user returns.
  registerHandler<IToolApprovalRequestPayload>({
    subscribe: (listener) => websocketService.onToolApprovalRequest(listener),
    handler: createBgCacheUpdater({
      update: ({ message, pages, payload }) => {
        setMessageToolCall({
          pages,
          uuid: payload.uuid,
          toolName: payload.toolName,
          args: payload.args,
        });
        upsertToolApprovalRequestToMessage({ message, payload });
      },
    }),
  });
  registerHandler<IToolApprovalRespondedPayload>({
    subscribe: (listener) => websocketService.onToolApprovalResponded(listener),
    handler: createBgCacheUpdater({
      update: ({ message, payload }) => {
        applyToolApprovalRespondedToMessage({ message, payload });
      },
    }),
  });

  registerHandler<IToolFilePayload>({
    subscribe: (listener) => websocketService.onToolFile(listener),
    handler: createBgCacheUpdater({
      update: ({ message, payload }) => {
        // NOTE: NSAI-5908 — a new array, never a push into the one the cache
        // holds: the clone is deep, but the intent must read as immutable.
        message.toolFiles = [...(message.toolFiles ?? []), payload.file];
      },
    }),
  });

  registerHandler<{ chatUuid: string }>({
    subscribe: (listener) => websocketService.onMessageStopped(listener),
    handler: (payload) => {
      if (payload.chatUuid !== chatId) return;
      queryClient.setQueryData<InfiniteData<IChatHistoryPage>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;
          return cancelSearchProgressForChat(oldData);
        },
      );
    },
  });

  // NOTE: Safety timeout — auto-cleanup if no terminal event arrives
  const timeoutId = setTimeout(() => {
    console.warn("WebSocket: Background tracking timeout for chat:", chatId);
    cleanupBackgroundTracker(chatId);
  }, BACKGROUND_TRACKING_TIMEOUT_MS);

  backgroundTrackers.set(chatId, { chatId, unsubscribers, timeoutId });
};

// NOTE: Unsubscribe handlers but do NOT leave the room — the hook will manage room membership
export const stopBackgroundTracking = (chatId: string): void => {
  const tracker = backgroundTrackers.get(chatId);
  if (!tracker) return;

  console.warn("WebSocket: Stopping background tracking for chat:", chatId);

  for (const unsubscribe of tracker.unsubscribers) {
    unsubscribe();
  }
  clearTimeout(tracker.timeoutId);
  backgroundTrackers.delete(chatId);
};

// NOTE: NSAI-5117 — unwinds every tracker, room membership included. Mirrors
// the loop the local web class ran at the top of `disconnect()`.
export const stopAllBackgroundTracking = (): void => {
  for (const [chatId] of [...backgroundTrackers]) {
    cleanupBackgroundTracker(chatId);
  }
};

export const isBackgroundTracking = (chatId: string): boolean => {
  return backgroundTrackers.has(chatId);
};

const cleanupBackgroundTrackerIfIdle = (chatId: string): void => {
  const cachedData = queryClient.getQueryData<InfiniteData<IChatHistoryPage>>(
    chatHistoryQueryKey(chatId),
  );

  // NOTE: Uses hasActiveWorkInPages (not hasActiveStreamInPages) so that
  // pending tool approvals keep the tracker alive — agents waiting for
  // user confirmation must not lose the socket room.
  const hasActiveWork = cachedData
    ? hasActiveWorkInPages(cachedData.pages)
    : false;

  if (hasActiveWork) {
    return;
  }

  cleanupBackgroundTracker(chatId);
};

const cleanupBackgroundTracker = (chatId: string): void => {
  const tracker = backgroundTrackers.get(chatId);
  if (!tracker) return;

  console.warn("WebSocket: Cleaning up background tracker for chat:", chatId);

  for (const unsubscribe of tracker.unsubscribers) {
    unsubscribe();
  }
  clearTimeout(tracker.timeoutId);
  backgroundTrackers.delete(chatId);
  websocketService.leaveChat(chatId);
};
